import {
  AUTH0_AUDIENCE,
  AUTH0_CLIENT_DOMAIN,
  AUTH0_CLIENT_ID,
  AUTH0_TENANT_DOMAIN,
} from '../common/constants/env.constants';

import { IAuth0TokenResponse } from '../authentication/interfaces/auth0token.interface';
import axios from 'axios';

export const buildAuth0LoginUrl = (code_challenge?: string, redirect_uri?: string) => {
  return (
    `https://${AUTH0_CLIENT_DOMAIN}/authorize` +
    `?response_type=code` +
    `&client_id=${AUTH0_CLIENT_ID}` +
    `&redirect_uri=${redirect_uri ?? globalThis.origin + '/auth-callback'}` +
    `&scope=openid profile email offline_access Mail.ReadWrite MailboxSettings.ReadWrite` +
    `&audience=${AUTH0_AUDIENCE}` +
    `&prompt=login` +
    `&code_challenge=${code_challenge ?? ''}` +
    `&code_challenge_method=S256`
  );
};

/**
 * Page on the add-in's own domain that forwards the login dialog to Auth0. Office checks only the
 * dialog's start URL against the manifest's AppDomains; once open, the dialog may move to any
 * domain. Starting here lets the login use the custom domain (login.csihelsinki.*) with manifests
 * that predate it.
 */
export const buildLoginDialogStartUrl = (code_challenge: string) =>
  `${globalThis.location.origin}/authentication/authentication.html` +
  `?code_challenge=${encodeURIComponent(code_challenge)}`;

/** PKCE S256 challenges are base64url; anything else is not passed on to Auth0. */
export const readCodeChallenge = (search: string): string | undefined => {
  const value = new URLSearchParams(search).get('code_challenge');
  return value && /^[A-Za-z0-9_-]{43,128}$/.test(value) ? value : undefined;
};

// The task pane navigates here itself, which Office allows only for AppDomains, so this uses the
// tenant domain that every installed manifest lists.
export const auth0LogoutUrl = () => {
  return (
    `https://${AUTH0_TENANT_DOMAIN}/v2/logout` +
    `?client_id=${AUTH0_CLIENT_ID}` +
    `&returnTo=${globalThis.origin}`
  );
};

export const handleAuthorizationCode = async (authorizationCode: string) => {
  const verifier = sessionStorage.getItem('@auth/code-verifier');
  const options = {
    method: 'POST',
    url: `https://${AUTH0_CLIENT_DOMAIN}/oauth/token`,
    headers: { 'content-type': 'application/x-www-form-urlencoded' },
    data: new URLSearchParams({
      grant_type: 'authorization_code',
      client_id: `${AUTH0_CLIENT_ID}`,
      code: authorizationCode,
      redirect_uri: globalThis.origin + '/authentication/authRedirect.html',
      code_verifier: verifier ?? '',
    }),
  };

  try {
    const response = await axios.request(options);
    if (response.data) {
      const { refresh_token, access_token } = response.data;
      const auth0TokenResponse: IAuth0TokenResponse = { refresh_token, access_token };
      return auth0TokenResponse;
    }
  } catch (error) {
    console.error('Error handling auth0 authorization code', error);
  }
  return undefined;
};

export const refreshAccessToken = async (refreshToken: string) => {
  const options = {
    method: 'POST',
    url: `https://${AUTH0_CLIENT_DOMAIN}/oauth/token`,
    headers: { 'content-type': 'application/x-www-form-urlencoded' },
    data: new URLSearchParams({
      grant_type: 'refresh_token',
      client_id: `${AUTH0_CLIENT_ID}`,
      refresh_token: refreshToken,
    }),
  };

  try {
    const response = await axios.request(options);
    if (response.data) {
      const { access_token, refresh_token } = response.data;
      const auth0TokenResponse: IAuth0TokenResponse = {
        refresh_token,
        access_token,
      };
      return { auth0TokenResponse };
    }
  } catch (error) {
    console.error('Error refreshing access token', error);
    return { auth0TokenResponse: null };
  }

  return { auth0TokenResponse: null };
};
